import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import Loader from "../../components/Loaders/Loader";
import {
  BUTTON_NEW_CAMPAIGN,
  LOADING_INFORMATION,
  FILTER_BY_DATES_PLACEHOLDER,
  ADS_CAMPAIGNS_DESCRIPTION,
  ADS_CAMPAIGNS_TITLE_DASHBOARD,
  DASHBOARD_METRICS_DELAY_WARNING,
  DASHBOARD_METRICS_DELAY_DISMISS,
  CAMPAIGNS_FOLDERS_MOVE,
  CAMPAIGNS_FOLDERS_MOVE_DESCRIPTION,
  CAMPAIGNS_FOLDERS_MOVE_ERROR,
  CAMPAIGNS_FOLDERS_MOVE_SUCCESS,
  CAMPAIGNS_FOLDERS_MOVE_TITLE,
  GALLERY_FOLDERS_ALL,
} from "../../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_ENTITIES,
  ADS_CAMPAIGN_STATUS_ACTIVE,
  ADS_CAMPAIGN_STATUS_DEACTIVATED,
  ALL_FILTER,
} from "../../utils/constants";
import { useStartCreateCampaign } from "../../hooks/useStartCreateCampaign";
import {
  startUpdateCampaignEntityStatus,
  startUpdateUserHideMetricsAlert,
} from "../../actions/adsCampaigns";
import { getNameBusinessTheme, getRoleCapability } from "../../actions/getters";
import Box from "@mui/material/Box";
import _ from "lodash";
import { Typography, useMediaQuery, Alert, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AdsCampaignsTable from "./components/AdsCampaignsTable";
import { getStatusAdsCampaign } from "../../services/adsCampaigns";
import { useInView } from "react-intersection-observer";
import Button from "../../components/Buttons/Button";
import { Helmet } from "react-helmet";
import AddIcon from "@mui/icons-material/Add";
import { useForm } from "react-hook-form";
import RangeDateField from "../../components/Form/RangeDateField";
import useStatisticsWithDateRange from "../../hooks/useStatisticsWithDateRange";
import { PILL_TRIGGER_VARIANT } from "../../components/Buttons/PillTriggerButton";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import useAdsCampaignsList from "../../hooks/useAdsCampaignsList";
import { useAdsCampaignFoldersSession } from "../../hooks/useAdsCampaignFoldersSession";
import { getCommonFolderID } from "../../utils/contentFolderUtils";
import GalleryFolderBar from "../../components/Gallery/GalleryFolderBar";
import ModalCreateContentFolder from "../../components/Modal/ModalCreateContentFolder";
import ModalMoveContentsToFolder from "../../components/Modal/ModalMoveContentsToFolder";
import ModalRenameContentFolder from "../../components/Modal/ModalRenameContentFolder";
import { buildOptimisticSwitchCopy } from "../../optimisticSwitch/copy";
import { OPTIMISTIC_SWITCH_KIND } from "../../optimisticSwitch/kinds";
import {
  runOptimisticSwitch,
  toOptimisticSwitchPayload,
} from "../../optimisticSwitch/runOptimisticSwitch";
import ModalCreateAdsCampaign from "../../components/Modal/ModalCreateAdsCampaign/ModalCreateAdsCampaign";
import {
  consumeCreateCampaignSearchParams,
  getCampaignOnboardingResumeFromAlert,
  stripCreateCampaignSearchParams,
  stripResumeCampaignSearchParam,
} from "../../components/Modal/ModalCreateAdsCampaign/createAdsCampaignWizardUtils";
import { RESUME_CAMPAIGN_ONBOARDING_QUERY_PARAM } from "../../components/Modal/ModalCreateAdsCampaign/createAdsCampaignWizardConstants";
import AgencyLinkingTipCard from "../../components/AgencyAccountLinking/AgencyLinkingTipCard";

const AdsCampaigns = () => {
  const [createCampaignModalOpen, setCreateCampaignModalOpen] = useState(false);
  const [createCampaignInitialCreatives, setCreateCampaignInitialCreatives] =
    useState(null);
  const [resumeCampaignSession, setResumeCampaignSession] = useState(null);
  const openedResumeKeyRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [metricsAlertClosed, setMetricsAlertClosed] = useState(false);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const onReadyToCreate = useCallback(
    ({ initialCreatives } = {}) => {
      setResumeCampaignSession(null);
      setCreateCampaignInitialCreatives(initialCreatives || null);
      setCreateCampaignModalOpen(true);
      setSearchParams((current) => stripResumeCampaignSearchParam(current), {
        replace: true,
      });
    },
    [setSearchParams]
  );
  const {
    startCreateCampaign,
    canCreateCampaign,
    brandInformation,
    isBrandReady,
  } = useStartCreateCampaign({
    fetchBrandOnMount: true,
    onReadyToCreate,
  });
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const canEditCampaign = dispatch(getRoleCapability("canEditCampaign"));
  const { theme } = useSelectorApp((state) => state.ui);
  const hideDelayMetricsAlert = useSelectorApp(
    (state) => state.user.hideDelayMetricsAlert
  );
  const [folderFilterID, setFolderFilterID] = useState(ALL_FILTER);
  const [folderMetricsRevision, setFolderMetricsRevision] = useState(0);
  const [selectedCampaignIDs, setSelectedCampaignIDs] = useState([]);

  const { control, watch, setValue } = useForm({
    defaultValues: {
      dateRange: {
        startDateTime: null,
        endDateTime: null,
      },
      createdBy: null,
    },
  });

  const { createdBy, dateRange } = watch();

  // Custom hook for campaigns list and sorting
  const {
    campaigns,
    onChangeCampaigns,
    isLoadingConfiguration,
    isLoadingCampaigns,
    sortConfig,
    onTableSort,
    loadedLastCampaign,
    onGetNextCampaigns,
    syncingCampaignIDs,
    datePresetKey,
    onChangeDatePresetKey,
    savedDateRange,
    folderCampaignIDs,
    noteFolderMembership,
  } = useAdsCampaignsList({
    folderID: folderFilterID,
  });
  const campaignsRef = useRef(campaigns);
  campaignsRef.current = campaigns;

  useEffect(() => {
    if (isLoadingConfiguration || !savedDateRange) return;

    setValue("dateRange", savedDateRange);
  }, [isLoadingConfiguration, savedDateRange, setValue]);

  const clearSelection = useCallback(() => {
    setSelectedCampaignIDs([]);
  }, []);

  const {
    folders,
    activeFolderID,
    isLoadingFolders,
    isCreatingFolder,
    isMovingContents,
    isRenamingFolder,
    onSelectFolder,
    onDropMany,
    moveContents,
    isOpenCreateFolderModal,
    openCreateFolderModal,
    closeCreateFolderModal,
    isOpenMoveFolderModal,
    openMoveFolderModal,
    closeMoveFolderModal,
    onCreateFolderInContext,
    createFolderParentID,
    createFolderParentLabel,
    getFolderLabel,
    isOpenRenameFolderModal,
    openRenameFolderModal,
    closeRenameFolderModal,
    renameFolderTarget,
    onRenameFolder,
    onDeleteFolder,
  } = useAdsCampaignFoldersSession({
    folderFilterID,
    onFolderFilterChange: setFolderFilterID,
    setCampaigns: onChangeCampaigns,
    clearSelection,
    getPreviousFolderID: (campaignID) => {
      const currentCampaigns = campaignsRef.current || {};
      const directCampaign = currentCampaigns[campaignID];
      if (directCampaign) return directCampaign.folderID || null;

      const matchedCampaign = Object.entries(currentCampaigns).find(
        ([key, campaign]) => String(campaign?.id || key) === String(campaignID)
      );
      return matchedCampaign?.[1]?.folderID || null;
    },
    onFolderMembershipChange: ({ campaignIDs, folderID }) => {
      noteFolderMembership({ campaignIDs, folderID });
      if (folderFilterID && folderFilterID !== ALL_FILTER) {
        setFolderMetricsRevision((current) => current + 1);
      }
    },
  });

  const onDatePresetChange = useCallback(
    (presetKey, dateEpochs = {}) => {
      onChangeDatePresetKey({
        presetKey,
        startDate: dateEpochs?.startDate ?? null,
        endDate: dateEpochs?.endDate ?? null,
      });
    },
    [onChangeDatePresetKey]
  );

  const { ref, inView } = useInView({
    triggerOnce: false,
    rootMargin: "50px 0px",
  });

  const {
    statistics,
    previousPeriodStatistics,
    statisticsLoading,
    hasActiveDateRange,
  } = useStatisticsWithDateRange(campaigns, dateRange);

  const [columnVisibility, setColumnVisibility] = useState({
    impressions: false,
    cpm: false,
    clicks: true,
    cpc: false,
    ctr: false,
    results: true,
    cpa: false,
    spend: false,
    roas: false,
    frequency: false,
  });

  const toggleColumn = (column) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const onCloseDelayMetricsAlert = () => setMetricsAlertClosed(true);

  const onDismissMetricsAlert = async () => {
    await dispatch(startUpdateUserHideMetricsAlert(true));
  };

  const onRedirectToCreateCampaign = startCreateCampaign;
  const hasConsumedCreateQueryRef = useRef(false);
  const notifications = useSelectorApp((state) => state?.alerts?.notifications);
  const creativeJobsHydrated = useSelectorApp(
    (state) => state?.alerts?.creativeJobsHydrated
  );
  const resumeAlertID = searchParams.get(
    RESUME_CAMPAIGN_ONBOARDING_QUERY_PARAM
  );

  useEffect(() => {
    if (!isBrandReady) return;

    const { shouldOpen, initialCreatives } =
      consumeCreateCampaignSearchParams(searchParams);
    if (!shouldOpen) {
      hasConsumedCreateQueryRef.current = false;
      return;
    }

    if (hasConsumedCreateQueryRef.current) return;

    hasConsumedCreateQueryRef.current = true;
    const nextParams = stripCreateCampaignSearchParams(searchParams);
    setSearchParams(nextParams, { replace: true });
    startCreateCampaign({
      initialCreatives: initialCreatives || undefined,
    });
  }, [isBrandReady, searchParams, setSearchParams, startCreateCampaign]);

  useEffect(() => {
    if (!resumeAlertID) {
      openedResumeKeyRef.current = null;
      return;
    }
    if (!isBrandReady) return;
    if (openedResumeKeyRef.current === resumeAlertID) return;

    const alert = (notifications || []).find(
      (notification) =>
        notification?.id === resumeAlertID ||
        notification?.jobID === resumeAlertID
    );
    const resume = alert ? getCampaignOnboardingResumeFromAlert(alert) : null;

    if (!resume) {
      if (!creativeJobsHydrated) return;
      setSearchParams((current) => stripResumeCampaignSearchParam(current), {
        replace: true,
      });
      return;
    }

    openedResumeKeyRef.current = resumeAlertID;
    setResumeCampaignSession((current) =>
      current?.resumeKey === resumeAlertID
        ? current
        : { ...resume, resumeKey: resumeAlertID }
    );
    setCreateCampaignInitialCreatives(null);
    setSearchParams((current) => stripResumeCampaignSearchParam(current), {
      replace: true,
    });
    setCreateCampaignModalOpen(true);
  }, [
    creativeJobsHydrated,
    isBrandReady,
    notifications,
    resumeAlertID,
    setSearchParams,
  ]);

  const onChangeStatusCampaign = async (campaignID) => {
    const campaign = campaigns[campaignID];
    const statusCampaign = getStatusAdsCampaign({
      states: campaign.states,
      target: "general",
    });
    const newStatus =
      statusCampaign === ADS_CAMPAIGN_STATUS_ACTIVE
        ? ADS_CAMPAIGN_STATUS_DEACTIVATED
        : ADS_CAMPAIGN_STATUS_ACTIVE;
    const previousStatus = statusCampaign;
    const campaignName =
      campaign?.platformsData?.[campaign?.platforms?.[0]]?.name ||
      campaign?.name ||
      "";
    const copy = buildOptimisticSwitchCopy({
      t,
      kind: OPTIMISTIC_SWITCH_KIND.CAMPAIGN,
      name: campaignName,
      activate: newStatus === ADS_CAMPAIGN_STATUS_ACTIVE,
    });

    const response = await runOptimisticSwitch({
      copy,
      applyOptimistic: () => {
        onChangeCampaigns((prev) => {
          const currentCampaign = prev[campaignID] || campaign;
          return {
            ...prev,
            [campaignID]: {
              ...currentCampaign,
              states: {
                ...currentCampaign.states,
                general: newStatus,
              },
            },
          };
        });
      },
      revertOptimistic: () => {
        onChangeCampaigns((prev) => {
          const currentCampaign = prev[campaignID] || campaign;
          return {
            ...prev,
            [campaignID]: {
              ...currentCampaign,
              states: {
                ...currentCampaign.states,
                general: previousStatus,
              },
            },
          };
        });
      },
      action: ({ operationId }) =>
        dispatch(
          startUpdateCampaignEntityStatus({
            campaignID,
            entity: ADS_CAMPAIGN_ENTITIES.ALL_CAMPAIGNS,
            entityID: campaignID,
            status: newStatus,
            optimisticSwitch: toOptimisticSwitchPayload({ operationId, copy }),
          })
        ),
    });

    return response.ok;
  };

  // Handle infinite scroll
  useEffect(() => {
    if (!inView || loadedLastCampaign) return;
    if (!_.isEmpty(statisticsLoading)) return;

    onGetNextCampaigns();
    // eslint-disable-next-line
  }, [inView, loadedLastCampaign, statisticsLoading]);

  const onGetFilteredCampaigns = () => {
    let finalCampaigns = campaigns || {};

    if (_.isEmpty(createdBy)) {
      return finalCampaigns;
    }

    const filteredKeys = Object.keys(finalCampaigns).filter((campaignID) =>
      createdBy.includes(finalCampaigns[campaignID]?.createdBy)
    );
    const filteredObject = {};
    filteredKeys.forEach((key) => {
      filteredObject[key] = finalCampaigns[key];
    });
    return filteredObject;
  };

  const getEnrichedCampaigns = () => {
    const filteredCampaigns = onGetFilteredCampaigns();

    if (!hasActiveDateRange || !filteredCampaigns) {
      return filteredCampaigns;
    }

    // Enrich campaigns with date range statistics or use the existing statistics
    const enrichedCampaigns = {};
    Object.keys(filteredCampaigns).forEach((campaignID) => {
      const hasStatistics = statistics[campaignID];
      const hasPreviousStatistics = previousPeriodStatistics[campaignID];
      let finalStatistics = filteredCampaigns[campaignID].statistics;
      let finalPreviousStatistics =
        filteredCampaigns[campaignID].previousPeriodStatistics || {};

      if (hasStatistics) {
        let statisticsFormatted = {};
        Object.keys(hasStatistics).forEach((platform) => {
          const platformStatistics = hasStatistics[platform];
          const campaignStatistics = platformStatistics?.campaign || {};
          statisticsFormatted[platform] = campaignStatistics;
        });
        finalStatistics = statisticsFormatted;
      }

      if (hasPreviousStatistics) {
        let previousStatisticsFormatted = {};
        Object.keys(hasPreviousStatistics).forEach((platform) => {
          const platformStatistics = hasPreviousStatistics[platform];
          const campaignStatistics = platformStatistics?.campaign || {};
          previousStatisticsFormatted[platform] = campaignStatistics;
        });
        finalPreviousStatistics = previousStatisticsFormatted;
      }

      enrichedCampaigns[campaignID] = {
        ...filteredCampaigns[campaignID],
        statistics: finalStatistics,
        previousPeriodStatistics: finalPreviousStatistics,
      };
    });

    return enrichedCampaigns;
  };

  const filteredCampaigns = getEnrichedCampaigns();
  const hasCampaigns = !_.isEmpty(campaigns);
  const hasSomeCampaignActive =
    hasCampaigns &&
    Object.keys(campaigns || {}).some(
      (campaignID) =>
        campaigns?.[campaignID]?.states?.general &&
        campaigns?.[campaignID]?.states?.general === ADS_CAMPAIGN_STATUS_ACTIVE
    );

  const movingItems = useMemo(
    () =>
      selectedCampaignIDs
        .map((campaignID) => filteredCampaigns?.[campaignID])
        .filter((campaign) => campaign?.id || campaign),
    [filteredCampaigns, selectedCampaignIDs]
  );

  const disabledMoveFolderID = useMemo(
    () =>
      getCommonFolderID({
        items: movingItems.map((campaign) => ({
          folderID: campaign?.folderID || null,
        })),
      }),
    [movingItems]
  );

  const currentFolderLabelForMove = useMemo(() => {
    if (disabledMoveFolderID) {
      return getFolderLabel({ folderID: disabledMoveFolderID });
    }
    if (activeFolderID) {
      return getFolderLabel({ folderID: activeFolderID });
    }
    return t(GALLERY_FOLDERS_ALL);
  }, [activeFolderID, disabledMoveFolderID, getFolderLabel, t]);

  const onToggleCampaignSelection = useCallback((campaignID) => {
    setSelectedCampaignIDs((prev) =>
      prev.includes(campaignID)
        ? prev.filter((id) => id !== campaignID)
        : [...prev, campaignID]
    );
  }, []);

  const onCallbackMoveToFolder = async ({ folderID }) =>
    moveContents({
      campaignIDs: selectedCampaignIDs,
      folderID,
    });

  if (isLoadingConfiguration)
    return (
      <Loader fullWidth hasMessage={true} message={t(LOADING_INFORMATION)} />
    );

  return (
    <>
      <Helmet>
        <title>{`${theme.nameBusiness} - ${t(ADS_CAMPAIGNS_TITLE_DASHBOARD)}`}</title>
      </Helmet>

      {hasSomeCampaignActive &&
        !hideDelayMetricsAlert &&
        !metricsAlertClosed && (
          <Box
            sx={{
              position: "fixed",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1300,
              width: "90%",
              maxWidth: 600,
            }}
          >
            <Alert
              severity="info"
              onClose={onCloseDelayMetricsAlert}
              sx={{
                backgroundColor: "#fff",
                color: "text.primary",
                borderRadius: 2,
                boxShadow: "0 2px 16px rgba(0,0,0,0.15)",
                "& .MuiAlert-icon": { color: "info.main" },
              }}
              action={
                <IconButton
                  size="small"
                  aria-label="close"
                  onClick={onCloseDelayMetricsAlert}
                  sx={{ color: "text.secondary" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box>
                  {t(DASHBOARD_METRICS_DELAY_WARNING, { NAME_BUSINESS })}
                </Box>
                <Box
                  component="span"
                  onClick={onDismissMetricsAlert}
                  sx={{
                    textDecoration: "underline",
                    fontStyle: "italic",
                    cursor: "pointer",
                  }}
                >
                  {t(DASHBOARD_METRICS_DELAY_DISMISS)}
                </Box>
              </Box>
            </Alert>
          </Box>
        )}

      <ContainerPage hasMarginBottom={false} sx={{ height: "100%" }}>
        <AgencyLinkingTipCard />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr auto",
            },
            gridTemplateRows: {
              xs: "auto auto auto",
              md: "auto auto",
            },
            gridTemplateAreas: {
              xs: `
                "title"
                "subtitle"
                "actions"
              `,
              md: `
                "title actions"
                "subtitle subtitle"
              `,
            },
            width: "100%",
            px: 3,
            mt: 4,
            gap: 1,
            alignItems: { xs: "flex-start", md: "center" },
          }}
        >
          <Box sx={{ gridArea: "title" }}>
            <TitlePage>{t(ADS_CAMPAIGNS_TITLE_DASHBOARD)}</TitlePage>
          </Box>

          <Box sx={{ gridArea: "subtitle", pb: { xs: 1, md: 0 } }}>
            <Typography variant="body1" sx={{ color: "#878383" }}>
              {t(ADS_CAMPAIGNS_DESCRIPTION)}
            </Typography>
          </Box>

          <Box
            sx={{
              gridArea: "actions",
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              width: { xs: "100%", md: "auto" },
              justifyContent: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <RangeDateField
              showStartNowButton={false}
              defaultPlaceholderText={FILTER_BY_DATES_PLACEHOLDER}
              sx={{ width: { xs: "100%", sm: "auto" }, mr: 1 }}
              triggerVariant={PILL_TRIGGER_VARIANT.PILL}
              startIcon={
                <CalendarTodayIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
              }
              control={control}
              name="dateRange"
              mode="text"
              onSave={() => {}}
              initialPresetKey={datePresetKey}
              onPresetChange={onDatePresetChange}
              showIconText={true}
            />
            {canCreateCampaign && (
              <>
                <Button
                  fullWidth={isMobile}
                  onClick={onRedirectToCreateCampaign}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                >
                  {t(BUTTON_NEW_CAMPAIGN)}
                  <AddIcon
                    sx={{
                      color: `${theme.palette.primary.contrastText} !important`,
                      ml: 0.5,
                      width: 20,
                      height: 20,
                    }}
                  />
                </Button>
              </>
            )}
          </Box>
        </Box>
        <AdsCampaignsTable
          data={filteredCampaigns}
          loading={isLoadingCampaigns}
          onChangeStatusCampaign={onChangeStatusCampaign}
          lastItemRef={ref}
          loadedLastCampaign={loadedLastCampaign}
          columnVisibility={columnVisibility}
          onToggleColumn={toggleColumn}
          onSetColumnVisibility={setColumnVisibility}
          statisticsLoading={statisticsLoading}
          control={control}
          sortConfig={sortConfig}
          onTableSort={(columnKey) => onTableSort(columnKey, columnVisibility)}
          hasActiveDateRange={hasActiveDateRange}
          dateRange={dateRange}
          syncingCampaignIDs={syncingCampaignIDs}
          folderCampaignIDs={folderCampaignIDs}
          membershipRevision={folderMetricsRevision}
          folderID={
            folderFilterID && folderFilterID !== ALL_FILTER
              ? folderFilterID
              : null
          }
          selectedCampaignIDs={selectedCampaignIDs}
          onToggleCampaignSelection={onToggleCampaignSelection}
          canEditFolders={canEditCampaign}
          onOpenMoveFolder={openMoveFolderModal}
          onRemoveFromFolder={({ campaignIDs }) =>
            moveContents({ campaignIDs, folderID: null })
          }
          folderBar={
            <GalleryFolderBar
              folders={folders}
              pinBottomPadding
              activeFolderID={activeFolderID}
              selectedContentIDs={selectedCampaignIDs}
              canEdit={canEditCampaign}
              isLoading={
                isLoadingFolders || isCreatingFolder || isMovingContents
              }
              onSelectFolder={onSelectFolder}
              onOpenCreateFolder={openCreateFolderModal}
              onDropMany={onDropMany}
              onRenameFolder={openRenameFolderModal}
              onDeleteFolder={onDeleteFolder}
            />
          }
        />
      </ContainerPage>
      <ModalCreateContentFolder
        modalOpen={isOpenCreateFolderModal}
        folders={folders}
        parentFolderID={createFolderParentID}
        parentFolderLabel={createFolderParentLabel}
        isLoading={isCreatingFolder}
        onCloseModal={closeCreateFolderModal}
        onCallbackCreate={onCreateFolderInContext}
      />
      <ModalMoveContentsToFolder
        modalOpen={isOpenMoveFolderModal}
        folders={folders}
        currentFolderLabel={currentFolderLabelForMove}
        createParentFolderID={createFolderParentID}
        createParentFolderLabel={createFolderParentLabel}
        disabledFolderID={disabledMoveFolderID}
        isLoading={isMovingContents}
        isCreatingFolder={isCreatingFolder}
        onCloseModal={closeMoveFolderModal}
        onCallbackCreate={onCreateFolderInContext}
        onCallbackMove={onCallbackMoveToFolder}
        titleKey={CAMPAIGNS_FOLDERS_MOVE_TITLE}
        primaryButtonTextKey={CAMPAIGNS_FOLDERS_MOVE}
        descriptionKey={CAMPAIGNS_FOLDERS_MOVE_DESCRIPTION}
        successKey={CAMPAIGNS_FOLDERS_MOVE_SUCCESS}
        errorKey={CAMPAIGNS_FOLDERS_MOVE_ERROR}
      />
      <ModalRenameContentFolder
        modalOpen={isOpenRenameFolderModal}
        folders={folders}
        folder={renameFolderTarget}
        isLoading={isRenamingFolder}
        onCloseModal={closeRenameFolderModal}
        onCallbackRename={onRenameFolder}
      />
      {createCampaignModalOpen && (
        <ModalCreateAdsCampaign
          modalOpen={createCampaignModalOpen}
          brandInformation={brandInformation}
          initialCreatives={createCampaignInitialCreatives}
          resumeSession={resumeCampaignSession}
          onCloseModal={() => {
            setCreateCampaignModalOpen(false);
            setCreateCampaignInitialCreatives(null);
            setResumeCampaignSession(null);
            hasConsumedCreateQueryRef.current = false;
          }}
        />
      )}
    </>
  );
};

export default AdsCampaigns;
